import styled, { keyframes } from 'styled-components'

/**
 * CSS stand-ins for the library's Lottie animations, which need a WebAssembly
 * player the CSP does not allow to compile. They echo the composer's ring: the
 * brand's two signal colours, moving only while the agent is working.
 */

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30% { transform: translateY(-4px); opacity: 1; }
`

const shimmer = keyframes`
  to { background-position: -200% center; }
`

export const StyledTyping = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 var(--m-spacing-1);

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      rgb(var(--m-color-brand-accent-1)),
      rgb(var(--m-color-brand-accent-2))
    );
    animation: ${bounce} 1.2s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      opacity: 0.7;
    }
  }
`

/** A compact box the user can open, the same shape as the library's own. */
export const StyledWork = styled.div`
  align-self: flex-start;
  min-width: 220px;
  max-width: 100%;
  padding: var(--m-spacing-2) var(--m-spacing-3);
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
  background-color: rgb(var(--m-color-background-primary));
`

export const StyledWorkToggle = styled.button`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  width: 100%;
  padding: var(--m-spacing-1) 0;
  border: 0;
  background: none;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  text-align: left;
  color: rgb(var(--m-color-text-placeholder));
  cursor: pointer;

  m-icon {
    --m-icon-color: rgb(var(--m-color-text-placeholder));
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`

export const StyledSpinner = styled.span`
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: conic-gradient(
    rgb(var(--m-color-brand-accent-1)),
    rgb(var(--m-color-brand-accent-2)),
    rgba(var(--m-color-brand-accent-2), 0)
  );
  -webkit-mask: radial-gradient(
    farthest-side,
    transparent calc(100% - 2.5px),
    #000 calc(100% - 2px)
  );
  mask: radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px));
  animation: ${spin} 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const StyledSteps = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-1);
  margin: var(--m-spacing-1) 0 0;
  padding: 0;
  list-style: none;
`

export const StyledStep = styled.li`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-placeholder));

  m-icon {
    --m-icon-color: rgb(var(--m-color-text-disabled));

    flex: none;
  }
`

/** Where a finished step's tick sits, so a running one lines up with it. */
export const StyledStepSpacer = styled.span`
  flex: none;
  width: 14px;
`

/** A running step shimmers, as the library's does. */
export const StyledRunningLabel = styled.span`
  background: linear-gradient(
      90deg,
      rgb(var(--m-color-text-placeholder)) 35%,
      rgb(var(--m-color-primary-100)) 50%,
      rgb(var(--m-color-text-placeholder)) 65%
    )
    0 center / 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 1.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    -webkit-text-fill-color: currentColor;
  }
`
